import api from './api';

/**
 * Privacy Service for Studio Dashboard
 * Handles GDPR compliance features
 */
class PrivacyService {
  /**
   * Export user data
   */
  async exportUserData(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      console.log('📦 Requesting data export...');
      
      const response = await api.post('/privacy/export-data');
      
      if (response.data) {
        // Trigger download
        this.downloadJSON(response.data, this.generateFilename());
        
        return {
          success: true,
          data: response.data
        };
      }
      
      return {
        success: false,
        error: 'No data received'
      };
    } catch (error: any) {
      console.error('❌ Data export failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Export failed'
      };
    }
  }

  /**
   * Get data summary
   */
  async getDataSummary(): Promise<any> {
    try {
      const response = await api.get('/privacy/data-summary');
      return response.data.summary;
    } catch (error) {
      console.error('❌ Get data summary failed:', error);
      throw error;
    }
  }

  /**
   * Request account deletion
   */
  async deleteAccount(reason?: string, feedback?: string): Promise<{ success: boolean; error?: string; deletionDate?: string }> {
    try {
      console.log('🗑️ Requesting account deletion...');
      
      const response = await api.post('/privacy/delete-account', {
        reason,
        feedback
      });
      
      return {
        success: true,
        deletionDate: response.data.deletion_date
      };
    } catch (error: any) {
      console.error('❌ Account deletion failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Deletion request failed'
      };
    }
  }

  /**
   * Update marketing consent
   */
  async updateMarketingConsent(consent: boolean): Promise<boolean> {
    try {
      await api.put('/privacy/marketing-consent', { consent });
      console.log(`✅ Marketing consent updated: ${consent}`);
      return true;
    } catch (error) {
      console.error('❌ Update consent failed:', error);
      return false;
    }
  }

  /**
   * Get consent status
   */
  async getConsentStatus(): Promise<any> {
    try {
      const response = await api.get('/privacy/consent-status');
      return response.data.consents;
    } catch (error) {
      console.error('❌ Get consent status failed:', error);
      throw error;
    }
  }

  /**
   * Download JSON data as file
   */
  private downloadJSON(data: any, filename: string) {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Generate export filename
   */
  private generateFilename(): string {
    const timestamp = new Date().toISOString().split('T')[0];
    return `beemi-data-export-${timestamp}.json`;
  }
}

export default new PrivacyService();
